import React, { useState } from "react";
import type { ChemicalVendor } from "../../types";
import { HOLIDAY_BITS, MESSAGES } from "../../constants/const";
import { showErrorMessage } from "../appPane";
import InputText from "../inputText";
import InputNumText from "../inputNumText";
import InputNumTextRange from "../inputNumTextRange";
import ModalHeader from "./modalHeader";
import TextareaAutosize from "react-textarea-autosize";
import InputEmailText from "../inputEmailText";

// 薬剤販売業者 変更/追加登録モーダル
interface Props {
  vendor?: ChemicalVendor;
  onClose: () => void;
  onRegister: (vendor: ChemicalVendor) => void;
}

/**
 * 薬剤販売業者登録更新ポップアップ
 * @param props
 * @returns
 */
export const VendorModal: React.FC<Props> = ({ vendor, onClose, onRegister }) => {
  // 郵便番号
  const posts = vendor?.vendorPost?.split("-");
  const [post1, setPost1] = useState(posts ? posts[0] : "");
  const [post2, setPost2] = useState(posts ? posts[1] : "");
  // 電話番号
  const tels = vendor?.vendorTel?.split("-");
  const [tel1, setTel1] = useState(tels ? tels[0] : "");
  const [tel2, setTel2] = useState(tels ? tels[1] : "");
  const [tel3, setTel3] = useState(tels ? tels[2] : "");
  // FAX
  const faxs = vendor?.vendorFax?.split("-");
  const [fax1, setFax1] = useState(faxs ? faxs[0] : "");
  const [fax2, setFax2] = useState(faxs ? faxs[1] : "");
  const [fax3, setFax3] = useState(faxs ? faxs[2] : "");
  // 担当者情報
  const [person, setPerson] = useState(vendor?.vendorPerson ?? "");
  const personTels = vendor?.vendorPersonTel?.split("-");
  const [personTel1, setPersonTel1] = useState(personTels ? personTels[0] : "");
  const [personTel2, setPersonTel2] = useState(personTels ? personTels[1] : "");
  const [personTel3, setPersonTel3] = useState(personTels ? personTels[2] : "");
  const [PersonMail, setPersonMail] = useState(vendor?.vendorPersonMail ?? "");
  // その他の入力項目
  const [name, setName] = useState(vendor?.vendorName ?? "");
  const [addr1, setAddr1] = useState(vendor?.vendorAddress1 ?? "");
  const [addr2, setAddr2] = useState(vendor?.vendorAddress2 ?? "");
  const [vendorDeliverySupportDays, setVendorDeliverySupportDays] = useState<number>(
    vendor?.vendorDeliverySupportDays ?? 0
  );
  const initialLeadTime = Number(vendor?.vendorLeadTime);
  const [leadTime, setLeadTime] = useState<number | "">(
    Number.isNaN(initialLeadTime) ? "" : initialLeadTime
  );
  const [memo, setMemo] = useState(vendor?.vendorMemo ?? "");

  // チェックボックスがクリックされた時のハンドラー
  const handleCheckboxChange = (mask: number) => {
    setVendorDeliverySupportDays(prevFlags => {
      // 排他的論理和（^）を使って、ビットを反転（トグル）させる
      return prevFlags ^ mask;
    });
  };

  // フォーム送信
  const sendForm = async (e: React.FormEvent) => {
    e.preventDefault();

    if ([name, tel1, person].find(v => !v || v === "")) {
      showErrorMessage(MESSAGES.ERROR_INPUT_EMPTY);
      return;
    }

    const vendorPost = [post1, post2].join("-");
    const vendorTel = [tel1, tel2, tel3].join("-");
    const vendorFax = [fax1, fax2, fax3].join("-");
    const vendorPersonTel = [personTel1, personTel2, personTel3].join("-");

    const data: ChemicalVendor = {
      vendorId: vendor?.vendorId,
      vendorName: name,
      vendorPost,
      vendorAddress1: addr1,
      vendorAddress2: addr2,
      vendorTel,
      vendorFax,
      vendorPerson: person,
      vendorPersonTel,
      vendorPersonMail: PersonMail,
      vendorDeliverySupportDays,
      vendorLeadTime: leadTime === "" ? 0 : leadTime,
      vendorMemo: memo,
    };
    onRegister(data);
  };

  return (
    <div className="modal">
      <div className="content">
        <ModalHeader title="薬剤販売業者" onClose={onClose} />
        <form
          onSubmit={sendForm}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
          }}
        >
          {/* 左の列 */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div className="parts">
              <label>会社名：</label>
              <InputText val={name} onChange={setName} />
            </div>

            <div className="parts">
              <label>郵便番号：</label>
              <InputNumText val={post1} len={3} onChange={setPost1} />
              -
              <InputNumText val={post2} len={4} onChange={setPost2} />
            </div>

            <div className="parts">
              <label>住所１：</label>
              <TextareaAutosize
                value={addr1}
                onChange={e => setAddr1(e.target.value)}
                style={{ minWidth: "45ch" }}
              />
            </div>

            <div className="parts">
              <label>住所２：</label>
              <TextareaAutosize
                value={addr2}
                onChange={e => setAddr2(e.target.value)}
                style={{ minWidth: "45ch" }}
              />
            </div>

            <div className="parts">
              <label>電話番号：</label>
              <InputNumTextRange val={tel1} min={2} max={4} onChange={setTel1} />
              -
              <InputNumTextRange val={tel2} min={2} max={4} onChange={setTel2} />
              -
              <InputNumText val={tel3} len={4} onChange={setTel3} />
            </div>

            <div className="parts">
              <label>FAX番号：</label>
              <InputNumTextRange val={fax1} min={2} max={4} onChange={setFax1} />
              -
              <InputNumTextRange val={fax2} min={2} max={4} onChange={setFax2} />
              -
              <InputNumText val={fax3} len={4} onChange={setFax3} />
            </div>
          </div>

          {/* 右の列 */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div className="parts">
              <label>担当者名：</label>
              <InputText val={person} onChange={setPerson} />
            </div>

            <div className="parts">
              <label>担当者電話番号：</label>
              <InputNumTextRange val={personTel1} min={2} max={4} onChange={setPersonTel1} />
              -
              <InputNumTextRange val={personTel2} min={2} max={4} onChange={setPersonTel2} />
              -
              <InputNumText val={personTel3} len={4} onChange={setPersonTel3} />
            </div>

            <div className="parts">
              <label>担当者メールアドレス：</label>
              <InputEmailText val={PersonMail} onChange={setPersonMail} />
            </div>

            <div className="parts">
              <label style={{ padding: "0.75rem 0" }}>搬入対応可：</label>
              <input
                type="checkbox"
                id="saturday"
                checked={(vendorDeliverySupportDays & HOLIDAY_BITS.SATURDAY) !== 0}
                onChange={() => handleCheckboxChange(HOLIDAY_BITS.SATURDAY)}
              />
              <label htmlFor="saturday" style={{ minWidth: "40px" }}>
                土曜
              </label>
              <input
                type="checkbox"
                id="sunday"
                checked={(vendorDeliverySupportDays & HOLIDAY_BITS.SUNDAY) !== 0}
                onChange={() => handleCheckboxChange(HOLIDAY_BITS.SUNDAY)}
              />
              <label htmlFor="sunday" style={{ minWidth: "40px" }}>
                日曜
              </label>
              <input
                type="checkbox"
                id="holiday"
                checked={(vendorDeliverySupportDays & HOLIDAY_BITS.HOLIDAY) !== 0}
                onChange={() => handleCheckboxChange(HOLIDAY_BITS.HOLIDAY)}
              />
              <label htmlFor="holiday" style={{ minWidth: "40px" }}>
                祝日
              </label>
            </div>

            <div className="parts">
              <label>リードタイム：</label>
              <input
                type="number"
                min="0"
                value={leadTime}
                onChange={e => {
                  const nextLeadTime = Number(e.target.value);
                  setLeadTime(
                    e.target.value === "" || Number.isNaN(nextLeadTime) ? "" : nextLeadTime
                  );
                }}
              />
              日
            </div>

            <div className="parts">
              <label>メモ：</label>
              <TextareaAutosize
                value={memo}
                onChange={e => setMemo(e.target.value)}
                style={{ minWidth: "300px" }}
              />
            </div>
          </div>

          <div className="parts actions" style={{ gridColumn: "2 / 3" }}>
            <button type="button" onClick={onClose} className="negative">
              キャンセル
            </button>
            <button type="submit" className="positive">
              登録
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorModal;
